package com.milta.feature.importjobs.presentation

import com.milta.core.auth.SessionEvent
import com.milta.core.cache.LocalCacheStore
import com.milta.core.connectivity.ConnectivityObserver
import com.milta.core.lifecycle.appForegroundEvents
import com.milta.core.network.ApiException
import com.milta.core.observability.ErrorReporting
import com.milta.core.observability.ErrorReporting.isConnectivityFailure
import com.milta.feature.auth.domain.repository.AuthRepository
import com.milta.feature.importjobs.domain.entity.ImportJob
import com.milta.feature.importjobs.domain.entity.ImportJobFlow
import com.milta.feature.importjobs.domain.usecase.AcknowledgeImportJobUseCase
import com.milta.feature.importjobs.domain.usecase.CancelImportJobUseCase
import com.milta.feature.importjobs.domain.usecase.GetImportJobUseCase
import com.milta.feature.importjobs.domain.usecase.ListActiveImportJobsUseCase
import com.milta.feature.importjobs.domain.usecase.RetryImportJobUseCase
import com.milta.feature.importjobs.domain.usecase.SubmitImportJobUseCase
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlin.coroutines.cancellation.CancellationException

/** What the app knows about the account's import — one at a time (business rule). */
data class ImportJobTrackerState(
    /** The running import, or the finished one not yet acknowledged; null when there is none. */
    val current: ImportJob? = null,
    /** Last call failed for lack of connection (confirmed by `isConnectivityFailure()`); cleared on the next success. */
    val isOffline: Boolean = false,
    /** A file is being sent (before the 202): the app blocks navigation until it is received or cut. */
    val isUploading: Boolean = false,
)

/**
 * App-wide follow-up of the asynchronous imports (`/api/v1/import-jobs`), created once as an eager
 * Koin `single` (see `importJobsModule`) — same role as [com.milta.feature.route.presentation.OfflineSyncViewModel]
 * hoisted at app level, so an import keeps being followed whatever screen is shown.
 *
 * - Polls `GET /import-jobs/{id}` every [POLL_INTERVAL_MS] while [ImportJobTrackerState.current] is
 *   Pending/Processing, then stops. Paused while [ConnectivityObserver] reports the device offline.
 * - Re-reads `GET /import-jobs?active=true` when the session starts, on every return to the
 *   foreground ([appForegroundEvents]) and when the network comes back — never while offline.
 * - Never calls the API outside an authenticated session: an unauthenticated call would make the
 *   refresh flow of `MiltaApiClient.kt` emit a spurious `SessionExpired`. Logout/expiry stops
 *   everything and forgets the current import.
 *
 * Actions ([submit], [retry], [cancel], [acknowledge]) go through the tracker so its state stays the
 * single source of truth; their [ApiException]s are rethrown for the calling screen to map.
 * State is confined to [dispatcher] (main thread by default).
 *
 * Anti-duplicate: once its deliveries were added, an import is [markConsumed] — its id is kept in
 * [localCacheStore] (survives a restart), it disappears at once and is ignored in every
 * `?active=true` answer, so it can never be validated twice. Its acknowledge is retried on each
 * re-read (session start, foreground, network back) until it succeeds, then the id is forgotten.
 */
class ImportJobTracker(
    private val submitImportJob: SubmitImportJobUseCase,
    private val getImportJob: GetImportJobUseCase,
    private val listActiveImportJobs: ListActiveImportJobsUseCase,
    private val retryImportJob: RetryImportJobUseCase,
    private val cancelImportJob: CancelImportJobUseCase,
    private val acknowledgeImportJob: AcknowledgeImportJobUseCase,
    private val authRepository: AuthRepository,
    private val connectivityObserver: ConnectivityObserver,
    private val localCacheStore: LocalCacheStore,
    private val foregroundEvents: Flow<Unit> = appForegroundEvents(),
    private val dispatcher: CoroutineDispatcher = Dispatchers.Main,
) {
    private val _state = MutableStateFlow(ImportJobTrackerState())
    val state: StateFlow<ImportJobTrackerState> = _state.asStateFlow()

    // Last-resort net, same as MviViewModel.scope: reported instead of crashing the app.
    private val scope = CoroutineScope(
        SupervisorJob() + dispatcher +
            CoroutineExceptionHandler { _, throwable -> ErrorReporting.report(throwable, source = SOURCE) },
    )

    private var sessionActive = false
    private var refreshJob: Job? = null
    private var pollJob: Job? = null
    private var polledId: String? = null

    /** Imports whose deliveries were added but whose acknowledge has not succeeded yet. */
    private val consumedIds: MutableSet<String> =
        localCacheStore.getString(CONSUMED_IDS_KEY)?.split(CONSUMED_IDS_SEPARATOR)?.filter { it.isNotBlank() }?.toMutableSet()
            ?: mutableSetOf()

    init {
        scope.launch {
            if (authRepository.isAuthenticated()) startSession()
            authRepository.sessionEvents.collect { event ->
                when (event) {
                    SessionEvent.LoggedIn -> startSession()
                    SessionEvent.LoggedOut, SessionEvent.SessionExpired -> stopSession()
                    is SessionEvent.SubscriptionExpired -> Unit
                }
            }
        }
        scope.launch {
            foregroundEvents.collect { if (sessionActive) refresh() }
        }
        scope.launch {
            // Network back: re-read right away instead of waiting for the next poll tick.
            var wasOnline = connectivityObserver.isOnline.value
            connectivityObserver.isOnline.collect { online ->
                if (online && !wasOnline && sessionActive) refresh()
                wasOnline = online
            }
        }
    }

    /** Re-reads the account's active import from the server (no-op outside a session or offline). */
    fun refresh() {
        scope.launch {
            if (!sessionActive || !connectivityObserver.isOnline.value) return@launch
            refreshJob?.cancel()
            refreshJob = launch {
                acknowledgeConsumed()
                val jobs = callApi { listActiveImportJobs() } ?: return@launch
                setCurrent(pickCurrent(jobs.filterNot { it.id in consumedIds }))
            }
        }
    }

    /**
     * The deliveries of import [id] were added to their route: hidden right away (never validated
     * twice), remembered on disk and acknowledged — retried on each re-read until it succeeds.
     */
    fun markConsumed(id: String) {
        scope.launch {
            consumedIds += id
            persistConsumedIds()
            forget(id)
            acknowledgeConsumed()
        }
    }

    private suspend fun acknowledgeConsumed() {
        if (!sessionActive) return
        for (id in consumedIds.toList()) {
            val done = try {
                acknowledgeImportJob(id)
                true
            } catch (e: CancellationException) {
                throw e
            } catch (e: ApiException) {
                // 404: not (or no longer) this account's import; 409: already acknowledged or not
                // acknowledgeable — retrying would never succeed.
                e.statusCode == HTTP_NOT_FOUND || e.statusCode == HTTP_CONFLICT
            } catch (e: Exception) {
                ErrorReporting.report(e, source = SOURCE)
                false
            }
            if (done) {
                consumedIds -= id
                persistConsumedIds()
            }
        }
    }

    private fun persistConsumedIds() {
        if (consumedIds.isEmpty()) {
            localCacheStore.remove(CONSUMED_IDS_KEY)
        } else {
            localCacheStore.putString(CONSUMED_IDS_KEY, consumedIds.joinToString(CONSUMED_IDS_SEPARATOR))
        }
    }

    /**
     * Uploads a file and starts following the new import. [routeId] is the existing route the
     * import is launched from (its deliveries always go there), null while creating a new route. On a 409 (another import already active)
     * the tracker re-reads the active import before rethrowing, so the screen can show it.
     */
    suspend fun submit(
        flow: ImportJobFlow,
        fileBytes: ByteArray,
        fileName: String,
        routeId: String?,
        onUpload: (sentBytes: Long, totalBytes: Long) -> Unit = { _, _ -> },
    ): ImportJob {
        _state.update { it.copy(isUploading = true) }
        try {
            val job = resyncOnConflict { submitImportJob(flow, fileBytes, fileName, routeId, onUpload) }
            // Not cancellable: once the 202 is in, the import must be known even if the screen cuts
            // the upload right now — it can then be cancelled, never left behind unseen.
            withContext(NonCancellable + dispatcher) {
                // A refresh started before the upload could answer without this job and clear it.
                refreshJob?.cancel()
                setCurrent(job)
            }
            return job
        } finally {
            _state.update { it.copy(isUploading = false) }
        }
    }

    /**
     * Relaunches a failed import from the file stored server-side, on its own flow's route.
     * A 409 (file expired, attempts exhausted, wrong status) re-reads the server state first.
     */
    suspend fun retry(job: ImportJob): ImportJob {
        val relaunched = resyncOnConflict { retryImportJob(job.flow, job.id) }
        onState { setCurrent(relaunched) }
        return relaunched
    }

    suspend fun cancel(id: String) {
        resyncOnConflict { cancelImportJob(id) }
        onState { forget(id) }
    }

    /** The user consumed the result (or the failure): the import leaves the active list. */
    suspend fun acknowledge(id: String) {
        resyncOnConflict { acknowledgeImportJob(id) }
        onState { forget(id) }
    }

    /** A 409 means the local view is stale (`error.import.*`): re-read before rethrowing to the screen. */
    private suspend fun <T> resyncOnConflict(block: suspend () -> T): T =
        try {
            block()
        } catch (e: ApiException) {
            if (e.statusCode == HTTP_CONFLICT) refresh()
            throw e
        }

    fun clear() {
        scope.cancel()
    }

    private fun startSession() {
        sessionActive = true
        refresh()
    }

    private fun stopSession() {
        sessionActive = false
        refreshJob?.cancel()
        stopPolling()
        _state.value = ImportJobTrackerState()
    }

    private fun forget(id: String) {
        if (_state.value.current?.id == id) setCurrent(null)
    }

    /** Running import first; otherwise the most recently updated finished one (ISO-8601 sorts as text). */
    private fun pickCurrent(jobs: List<ImportJob>): ImportJob? =
        jobs.firstOrNull { it.status.isInProgress } ?: jobs.maxByOrNull { it.updatedAt ?: it.createdAt }

    private fun setCurrent(job: ImportJob?) {
        _state.update { it.copy(current = job) }
        if (job != null && job.status.isInProgress) {
            if (polledId != job.id || pollJob?.isActive != true) startPolling(job.id)
        } else {
            stopPolling()
        }
    }

    private fun startPolling(id: String) {
        pollJob?.cancel()
        polledId = id
        pollJob = scope.launch {
            while (true) {
                delay(POLL_INTERVAL_MS)
                if (!sessionActive) break
                if (!connectivityObserver.isOnline.value) {
                    // Paused offline; the network-back collector in init re-reads right away,
                    // polling itself resumes on the next tick.
                    connectivityObserver.isOnline.first { it }
                    continue
                }
                var gone = false
                val job = callApi(onApiError = { if (it.statusCode == HTTP_NOT_FOUND) gone = true }) { getImportJob(id) }
                if (gone) {
                    // No longer this account's import (404): nothing left to follow.
                    _state.update { if (it.current?.id == id) it.copy(current = null) else it }
                    break
                }
                if (job == null) continue
                if (_state.value.current?.id != id) break
                _state.update { it.copy(current = job) }
                if (!job.status.isInProgress) break
            }
            polledId = null
        }
    }

    private fun stopPolling() {
        pollJob?.cancel()
        pollJob = null
        polledId = null
    }

    /**
     * Runs a background API call: null on failure. "Offline" only when [isConnectivityFailure]
     * confirms it; [CancellationException] always propagates; other failures go to [ErrorReporting]
     * (which already ignores 4xx).
     */
    private suspend fun <T> callApi(onApiError: (ApiException) -> Unit = {}, block: suspend () -> T): T? =
        try {
            block().also { _state.update { it.copy(isOffline = false) } }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            if (e is ApiException) onApiError(e)
            if (e.isConnectivityFailure()) {
                _state.update { it.copy(isOffline = true) }
            } else {
                ErrorReporting.report(e, source = SOURCE)
            }
            null
        }

    private suspend fun onState(block: () -> Unit) = withContext(dispatcher) { block() }

    private companion object {
        const val POLL_INTERVAL_MS = 3_000L
        const val HTTP_NOT_FOUND = 404
        const val HTTP_CONFLICT = 409
        const val SOURCE = "ImportJobTracker"
        const val CONSUMED_IDS_KEY = "milta_consumed_import_jobs"
        const val CONSUMED_IDS_SEPARATOR = ","
    }
}
